import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import logger from '@widgets/logger';
import { styled } from '@mui/material/styles';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { ColorPicker } from '@components/ColorPicker';
import TextEditor from '@components/TextEditor';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import { Switch } from '@components/Switch';
import { Select } from '@components/Select';
import { Input } from '@components/Input';
import { Typography } from '@components/Typography';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { CHANNEL } from '@constants/channels';
import { CAMPAIGN_TYPE } from '@constants/campaigns';
import { MB } from '@constants/file';
import { validateFileSize } from '@helpers/upload-file';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { useTranslation, useSurveysOptions, useWhatsAppBusinessTemplates, useCouponsTemplates } from '@hooks';
import { alertActions } from '@actions';
import { campaignsService } from '@services';
import { CtaWrapper } from './styles';

const StyledRadioButtons = styled(RadioButtonsGroup)({
    marginTop: '8px',
    marginBottom: '8px',
    gridColumn: '1 / span 1',
    '@media (max-width: 600px)': {
        gridColumn: '1 / span 2',
    },
});

export const MESSAGE_TYPE = {
    MESSAGE: 1,
    SURVEY: 2,
    COUPON: 3,
};

const ContentSource = ({ campaignType, channelType, brands, isView, messageType, setMessageType }) => {
    const [uploading, setUploading] = useState(false);

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { getSurveysOptionsByLocation, getSurveysOptionsByBrand } = useSurveysOptions();
    const { control, setValue, watch } = useFormContext();
    const { getWhatsAppBusinessTemplatesOptionsByChannel } = useWhatsAppBusinessTemplates();
    const { getCouponsOptionsByLocation, getCouponsOptionsByBrand } = useCouponsTemplates();

    const coverPhotoEnabled = watch('coverPhotoEnabled');
    const ctaButtonEnabled = watch('ctaButtonEnabled');
    const coverPhotoUrl = watch('coverPhotoUrl');
    const locationType = watch('locationType');
    const brandId = watch('brandId');
    const locationId = watch('locationId');
    const selectedSurveyTemplateId = watch('surveyTemplateId');
    const selectedChannelId = watch('channelId');
    const selectedCouponTemplateId = watch('couponTemplateId');

    const aiContextLocationId = useMemo(() => {
        return locationType === LOCATION_TYPE.BRAND
            ? brands.find(b => b.id === brandId)?.locations.find(l => l.isMainLocation)?.id
            : locationId;
    }, [locationType, brandId, brands, locationId]);

    const surveysOptions = useMemo(() => {
        if (campaignType !== CAMPAIGN_TYPE.ONGOING) return [];
        return locationType === LOCATION_TYPE.BRAND
            ? getSurveysOptionsByBrand(brandId)
            : getSurveysOptionsByLocation(locationId);
    }, [locationType, brandId, locationId]);

    const whatsappTemplatesOptions = useMemo(() => {
        return getWhatsAppBusinessTemplatesOptionsByChannel(selectedChannelId, {
            hasButtons: messageType === MESSAGE_TYPE.SURVEY || messageType === MESSAGE_TYPE.COUPON,
        });
    }, [selectedChannelId, messageType]);

    const couponsOptions = useMemo(() => {
        return locationType === LOCATION_TYPE.BRAND
            ? getCouponsOptionsByBrand(brandId)
            : getCouponsOptionsByLocation(locationId);
    }, [locationType, brandId, locationId]);

    useEffect(() => {
        if (surveysOptions.length > 0 && campaignType === CAMPAIGN_TYPE.ONGOING && (locationId || brandId)) {
            if (!surveysOptions.find(s => s.value === selectedSurveyTemplateId)) {
                setValue('surveyTemplateId', '', { shouldValidate: true });
            }
        }
    }, [selectedSurveyTemplateId, surveysOptions, locationId, brandId]);

    useEffect(() => {
        if (couponsOptions.length > 0 && selectedCouponTemplateId && (locationId || brandId)) {
            if (!couponsOptions.find(s => s.value === selectedCouponTemplateId)) {
                setValue('couponTemplateId', '', { shouldValidate: true });
            }
        }
    }, [selectedCouponTemplateId, couponsOptions, locationId, brandId]);

    const IS_EMAIL = channelType === CHANNEL.EMAIL;
    const IS_WHATSAPP_BETA = channelType === CHANNEL.WHATSAPP;
    const IS_WHATSAPP_BUSINESS = channelType === CHANNEL.WHATSAPP_BUSINESS;

    const uploadImage = async e => {
        try {
            const file = e.target.files[0];
            if (!file) return;
            const { error, metric, size } = validateFileSize(file, 15 * MB);
            if (error) {
                dispatch(alertActions.error(t('apiErrors.file_size_too_large', { size, metric })));
                return;
            }
            setUploading(true);
            const data = new FormData();
            data.append('file', file);
            data.append('fileName', file.name);
            const response = await campaignsService.uploadEmailCampaignImage(data);
            setValue('coverPhotoUrl', response.publicUrl, { shouldDirty: true, shouldValidate: true });
        } catch (_) {
            logger.error(`Couldn't upload coverPhotoUrl`);
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    };

    return (
        <>
            {/* <div style={{ gridColumn: '1 / span 1' }}> */}
            <Controller
                control={control}
                name="messageType"
                render={({ field }) => (
                    <StyledRadioButtons
                        id="pl-radiobuttons"
                        value={field.value}
                        onChange={value => {
                            setValue('messageType', value, { shouldDirty: true, shouldValidate: true });
                            setMessageType(value);
                            setValue('whatsAppTemplateId', '');
                            setValue('couponTemplateId', '');
                        }}
                        options={[
                            { value: MESSAGE_TYPE.MESSAGE, label: t('common.message', { count: 1 }) },
                            ...(campaignType === CAMPAIGN_TYPE.ONGOING
                                ? [{ value: MESSAGE_TYPE.SURVEY, label: t('common.survey') }]
                                : []),
                            { value: MESSAGE_TYPE.COUPON, label: t('common.coupon') },
                        ]}
                        disabled={isView}
                    />
                )}
            />
            {/* </div> */}
            {(messageType === MESSAGE_TYPE.MESSAGE || messageType === MESSAGE_TYPE.COUPON) && (
                <>
                    {IS_EMAIL && (
                        <Controller
                            control={control}
                            name="subject"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                                    label={t('common.subject')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                    disabled={isView}
                                    sx={{ gridColumn: '1 / span 2' }}
                                />
                            )}
                        />
                    )}
                    {!IS_WHATSAPP_BUSINESS && (
                        <Controller
                            control={control}
                            name="message"
                            render={({ field, fieldState: { error }, ref }) => (
                                <div style={{ gridColumn: '1 / span 2', position: 'relative' }}>
                                    <TextEditor
                                        {...field}
                                        ref={ref}
                                        label={t('common.message', { count: 1 })}
                                        limit={channelType === CHANNEL.WHATSAPP ? 1024 : 0}
                                        initialValue={field.value || ''}
                                        onChange={text =>
                                            setValue('message', parseTipTapJSONToPlainText(text), {
                                                shouldValidate: true,
                                                shouldDirty: true,
                                            })
                                        }
                                        editable={!isView}
                                        required
                                        error={error}
                                        helperText={error ? t(error.message, { count: 1024 }) : null}
                                        aiAssistant={{
                                            additionalOptions: [
                                                {
                                                    text: t('AIAssistant.writeCampaign'),
                                                    type: AITaskType.GenerateCampaignMessage,
                                                    taskType: AI_TASK_TYPE.CONTEXTUAL,
                                                    companyId: aiContextLocationId,
                                                },
                                            ],
                                        }}
                                    />
                                </div>
                            )}
                        />
                    )}
                    {messageType === MESSAGE_TYPE.COUPON && (
                        <Controller
                            control={control}
                            name="couponTemplateId"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) => {
                                        setValue('couponTemplateId', value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                    options={couponsOptions}
                                    required
                                    disabled={isView}
                                    label={t('common.coupon')}
                                    fullWidth
                                    style={{ gridColumn: '1 / span 2' }}
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                />
                            )}
                        />
                    )}
                </>
            )}
            {messageType === MESSAGE_TYPE.MESSAGE && IS_EMAIL && (
                <>
                    <Controller
                        control={control}
                        name="coverPhotoEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={field.value}
                                handleChange={event => {
                                    setValue('coverPhotoEnabled', event.target.checked, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                label={t('CampaignsPage.addCoverPhoto')}
                                style={{ gridColumn: '1 / span 2' }}
                                disabled={isView}
                            />
                        )}
                    />
                    {coverPhotoEnabled && (
                        <div style={{ gridColumn: '1 / span 2' }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    display: ' block',
                                    marginBottom: '8px',
                                }}
                            >
                                {t('CampaignsPage.coverPhotoFormat')}
                            </Typography>
                            <ImageUploaderSingle
                                extensions=".png, .jpg, .jpeg"
                                dimension={[600]}
                                maxSize={5 * MB}
                                logoUrl={coverPhotoUrl}
                                disabled={uploading || isView}
                                onDelete={() =>
                                    setValue('coverPhotoUrl', null, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                }
                                uploadLogo={uploadImage}
                                isUploading={uploading}
                            />
                        </div>
                    )}
                    <Controller
                        control={control}
                        name="ctaButtonEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={field.value}
                                handleChange={event => {
                                    setValue('ctaButtonEnabled', event.target.checked, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                label={t('CampaignsPage.addCTAButton')}
                                style={{ gridColumn: '1 / span 2' }}
                                disabled={isView}
                            />
                        )}
                    />
                    {ctaButtonEnabled && (
                        <CtaWrapper>
                            <Controller
                                control={control}
                                name="ctaTitle"
                                render={({ field, fieldState: { error }, ref }) => (
                                    <Input
                                        ref={ref}
                                        inputProps={{
                                            ...field,
                                            onChange: e => field.onChange(e.target.value),
                                        }}
                                        label={t('CampaignsPage.name')}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                        disabled={isView}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="ctaLink"
                                render={({ field, fieldState: { error }, ref }) => (
                                    <Input
                                        ref={ref}
                                        inputProps={{
                                            ...field,
                                            onChange: e => field.onChange(e.target.value),
                                        }}
                                        label={t('common.link')}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                        disabled={isView}
                                    />
                                )}
                            />
                            <Controller
                                control={control}
                                name="ctaColor"
                                render={({ field, fieldState: { error }, ref }) => (
                                    <ColorPicker
                                        ref={ref}
                                        inputProps={{
                                            ...field,
                                            onChange: e => {
                                                const val = e?.target?.value ? e.target.value : e;
                                                field.onChange(val);
                                            },
                                        }}
                                        label={t('common.color', { count: 1 })}
                                        fullWidth
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        required
                                        disabled={isView}
                                    />
                                )}
                            />
                        </CtaWrapper>
                    )}
                </>
            )}
            {messageType === MESSAGE_TYPE.SURVEY && (
                <Controller
                    control={control}
                    name="surveyTemplateId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('surveyTemplateId', value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            options={surveysOptions}
                            required
                            disabled={isView}
                            label={t('common.survey')}
                            fullWidth
                            style={{ gridColumn: '1 / span 2' }}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            )}
            {IS_WHATSAPP_BUSINESS && (
                <Controller
                    control={control}
                    name="whatsAppTemplateId"
                    render={({ field, fieldState: { error }, ref }) => (
                        <Select
                            {...field}
                            ref={ref}
                            onChange={({ target: { value } }) => {
                                setValue('whatsAppTemplateId', value, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                            }}
                            options={whatsappTemplatesOptions}
                            required
                            disabled={isView}
                            label={t('FeedbackOverview.template')}
                            fullWidth
                            style={{ gridColumn: '1 / span 2' }}
                            error={!!error}
                            helperText={error ? t(error.message) : null}
                        />
                    )}
                />
            )}
            {IS_WHATSAPP_BETA ? (
                <>
                    <Controller
                        control={control}
                        name="coverPhotoEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={field.value}
                                handleChange={event => {
                                    setValue('coverPhotoEnabled', event.target.checked, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    });
                                }}
                                label={t('CampaignsPage.addImage')}
                                style={{ gridColumn: '1 / span 2' }}
                                disabled={isView}
                            />
                        )}
                    />
                    {coverPhotoEnabled && (
                        <div style={{ gridColumn: '1 / span 2' }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.6)',
                                    display: ' block',
                                    marginBottom: '8px',
                                }}
                            >
                                {t('CampaignsPage.imageFormat')}
                            </Typography>
                            <ImageUploaderSingle
                                extensions=".png, .jpg, .jpeg"
                                logoUrl={coverPhotoUrl}
                                maxSize={5 * MB}
                                disabled={uploading || isView}
                                onDelete={() =>
                                    setValue('coverPhotoUrl', null, {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                    })
                                }
                                uploadLogo={uploadImage}
                                isUploading={uploading}
                            />
                        </div>
                    )}
                </>
            ) : null}
        </>
    );
};

export default ContentSource;
